import { useTccPairCurves } from '../../hooks/useFaultAnalysis.ts'
import { curveToPoints, logScale, type Scale } from '../../utils/tccChart.ts'
import { IndicativeBanner } from './indicative.tsx'

interface TccViewerProps {
  feederId: number
  upstreamDeviceId: number
  downstreamDeviceId: number
  onClose: () => void
}

const W = 360, H = 260, PAD = 40
const X: Scale = { domainMinLog: 1, domainMaxLog: 5, pxMin: PAD, pxMax: W - 10 }   // 10..100k A
const Y: Scale = { domainMinLog: -3, domainMaxLog: 3, pxMin: H - PAD, pxMax: 10 }  // 0.001..1000 s

export function TccViewer({ feederId, upstreamDeviceId, downstreamDeviceId, onClose }: TccViewerProps) {
  const { data, isLoading, error } = useTccPairCurves(feederId, upstreamDeviceId, downstreamDeviceId)

  if (isLoading) return <div className="text-sm text-gray-400 p-4">Loading curves...</div>
  if (error) return (
    <div className="text-sm text-red-600 p-4">
      Error loading TCC curves (request error, not an empty result).
    </div>
  )
  if (!data) return null

  const faultPx = data.fault_current_a != null ? logScale(data.fault_current_a, X) : null

  const line = (pts: number[][] | null, color: string, dash?: string) =>
    pts && pts.length > 1
      ? <polyline points={curveToPoints(pts, X, Y)} fill="none" stroke={color}
                  strokeWidth={1.5} strokeDasharray={dash} />
      : null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">TCC curves</h4>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">Close</button>
      </div>
      {data.indicative_only && <IndicativeBanner />}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full border border-gray-200 rounded bg-white">
        {/* axes */}
        <line x1={PAD} y1={10} x2={PAD} y2={H - PAD} stroke="#cbd5e1" />
        <line x1={PAD} y1={H - PAD} x2={W - 10} y2={H - PAD} stroke="#cbd5e1" />
        {/* fault-current band */}
        {faultPx != null && (
          <line x1={faultPx} y1={10} x2={faultPx} y2={H - PAD} stroke="#6b7280"
                strokeWidth={1} strokeDasharray="3 2" />
        )}
        {/* upstream min-melt (protected), downstream total-clear (protecting) */}
        {line(data.upstream.minimum_melt, '#e74c3c')}
        {line(data.downstream.total_clear, '#3498db')}
        {line(data.upstream.total_clear, '#e74c3c', '4 2')}
        {line(data.downstream.minimum_melt, '#3498db', '4 2')}
        <text x={PAD} y={H - PAD + 14} fontSize="8" fill="#6b7280">current (A, log)</text>
        <text x={4} y={16} fontSize="8" fill="#6b7280">time (s, log)</text>
      </svg>
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: '#e74c3c' }} /> Upstream {data.upstream.device_name ?? `#${data.upstream.device_id}`}</span>
        <span><span className="inline-block w-3 h-0.5 align-middle" style={{ background: '#3498db' }} /> Downstream {data.downstream.device_name ?? `#${data.downstream.device_id}`}</span>
        <span>solid = melt/clear boundary; dashed = the other curve</span>
      </div>
      {(data.upstream.minimum_melt == null || data.downstream.total_clear == null) && (
        <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1">
          No seeded TCC curve for one or both fuses — REQUIRES PE / TCC REVIEW.
        </div>
      )}
    </div>
  )
}
